import { useEffect, useState } from "react";
import { Check, Undo2 } from "lucide-react";
import { formatKr, fmtTime, relTime, fmtRange, fmtFrom, slotsOf } from "../lib/format";

export const OPEN = new Set(["reserved", "pending_confirm"]);

export function orderState(o, now) {
  const open = OPEN.has(o.status);
  const sl = slotsOf(o);
  const lastEnd = sl.length ? Date.parse(sl[sl.length - 1].end) : Date.parse(o.pickup_end);
  const active = sl.find((s) => Date.parse(s.start) <= now && now < Date.parse(s.end)) || null;
  const next = sl.find((s) => Date.parse(s.start) > now) || null;
  const overdue = open && lastEnd < now;
  return { open, overdue, notStarted: open && !overdue && !active && !!next, active, next };
}

function Meta({ o, st }) {
  return (
    <div className="pc-meta">
      {st.overdue && <span className="pc-badge red" data-testid={`pickup-overdue-${o.id}`}>Overskredet · for {relTime(o.pickup_end)}</span>}
      {st.notStarted && <span className="pc-time muted">Fra {fmtFrom(st.next.start)}–{fmtTime(st.next.end)}</span>}
      {st.open && st.active && (
        <span className="pc-time">
          {fmtRange(st.active.start, st.active.end)}
        </span>
      )}
      {o.status === "pending_confirm" && <span className="pc-badge amber">Ikke bekræftet</span>}
      {o.status === "picked_up" && <span className="pc-badge green">Udleveret kl. {fmtTime(o.picked_up_at)}{o.picked_by ? ` af ${o.picked_by}` : ""}</span>}
      {o.status === "no_show" && <span className="pc-badge red">Ikke hentet</span>}
      {o.comment_reminder === "sent" && <span className="pc-badge blue" data-testid={`pickup-reminded-${o.id}`}>Påmindet</span>}
    </div>
  );
}

const unitTxt = (ore) => `${formatKr(ore).replace(/ kr\.$/, "")} kr. pr. stk.`;

function UnitPrice({ o }) {
  const units = o.lines ? [...new Set(o.lines.map((l) => l.unit_ore ?? o.unit_ore))] : [o.unit_ore];
  if (units[0] == null) return null;
  return (
    <div className="pc-unit" data-testid={`pickup-unit-price-${o.id}`}>
      Ja tak-pris:{" "}
      {units.length === 1
        ? unitTxt(units[0])
        : o.lines.map((l) => `${l.variant_name} ${unitTxt(l.unit_ore ?? o.unit_ore)}`).join(" · ")}
    </div>
  );
}

export default function PickupCard({ o, now, onAct, onCustomer, highlight, big }) {
  const st = orderState(o, now);
  const [confirmNs, setConfirmNs] = useState(false);
  useEffect(() => {
    if (!confirmNs) return;
    const t = setTimeout(() => setConfirmNs(false), 4000);
    return () => clearTimeout(t);
  }, [confirmNs]);
  const cls = ["pcard", st.overdue && "overdue", o.status === "picked_up" && "done", o.status === "no_show" && "noshow", highlight && "hl", big && "big"]
    .filter(Boolean)
    .join(" ");
  return (
    <article className={cls} data-testid={`pickup-card-${o.id}`}>
      <span className="code pcode">{o.code}</span>
      <div className="pc-main">
        <h3>
          {onCustomer ? (
            <button type="button" className="pc-name" onClick={() => onCustomer(o.customer_id)} data-testid={`pickup-name-${o.id}`}>
              {o.name}
            </button>
          ) : (
            o.name
          )}
          {o.name_source === "customer" && <span className="by-customer" data-testid={`pickup-name-hint-${o.id}`}>(skrevet af kunden)</span>}
        </h3>
        <div className="pc-what">
          {o.cover && (
            <span className="pc-thumb" data-testid={`pickup-thumb-${o.id}`}>
              <img src={o.cover} alt="" />
              {o.has_video && <span className="thumb-play" aria-label="Video">▶</span>}
            </span>
          )}
          {o.lines ? (
            <><b data-testid={`pickup-lines-${o.id}`}>{o.lines.map((l) => `${l.qty} × ${l.variant_name}`).join(", ")}</b> · {o.title} · {formatKr(o.amount)}</>
          ) : (
            <><b>{o.qty} ×</b> {o.title}{o.variant_name ? ` (${o.variant_name})` : ""} · {formatKr(o.amount)}</>
          )}
        </div>
        <UnitPrice o={o} />
        {o.customer_note && <div className="pc-note muted" data-testid={`pickup-note-${o.id}`}>📝 {o.customer_note}</div>}
        <Meta o={o} st={st} />
      </div>
      <div className="pc-actions">
        {st.open && (
          <>
            <button className="btn primary pc-go" onClick={() => onAct(o, "pickup")} data-testid={`pickup-done-${o.id}`}>
              <Check size={18} /> Udleveret
            </button>
            <button
              className={`btn small pc-ns${confirmNs ? " confirm" : ""}`}
              onClick={() => (confirmNs ? onAct(o, "noshow") : setConfirmNs(true))}
              data-testid={`pickup-noshow-${o.id}`}
            >
              {confirmNs ? "Bekræft: ikke hentet" : "Ikke hentet"}
            </button>
          </>
        )}
        {o.status === "no_show" && (
          <button className="btn small" onClick={() => onAct(o, "pickup")} data-testid={`pickup-done-${o.id}`}>
            Udleveret alligevel
          </button>
        )}
        {o.status === "picked_up" && (
          <button className="btn small" onClick={() => onAct(o, "undo-any")} data-testid={`pickup-undo-${o.id}`}>
            <Undo2 size={15} /> Fortryd
          </button>
        )}
      </div>
    </article>
  );
}

export function UndoToast({ u, onUndo, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 6000);
    return () => clearTimeout(t);
  }, [u.key, onDone]);
  return (
    <div className={`undo-toast${u.kind === "noshow" ? " warn" : ""}`} role="status" data-testid="undo-toast">
      <span className="ut-icon" aria-hidden="true">
        <Check size={16} />
      </span>
      <span className="ut-text" data-testid="undo-toast-text">{u.text}</span>
      <button className="ut-btn" onClick={onUndo} data-testid="undo-toast-btn">
        Fortryd
      </button>
      <span className="ut-bar" key={u.key} />
    </div>
  );
}
